// ════════════════════════════════════════════════════════════════════
// ILLEGAL EMPIRE — HoloFoil  (THE ONE TRUE FOIL)
// ────────────────────────────────────────────────────────────────────
// A single, registry-driven holographic overlay. Drop <HoloFoil tier="…">
// over any card art ANYWHERE — booster shop, faction rewards, the big
// pull reveal, achievements, depths trophies — and the same tier looks
// the SAME everywhere. No more five-different-copies drift; no more
// "the faction J'ynxi doesn't match the shop J'ynxi" false advertising.
//
// ── WHY THIS EXISTS ──────────────────────────────────────────────────
// The premier J'ynxi treatment (rotating conic "diamond" shimmer + a
// diagonal light sweep + a breathing tier glow + gold corner flourishes)
// used to live hardcoded inside FactionHall as `jesters-holo-*`. Every
// other surface had its own lesser foil. This component lifts that
// premier look out, makes it the baseline contract, and lets EACH TIER
// define its own recipe — so the whole foil family is consistent across
// the app AND every tier can be made more special than the last.
//
// ── THE DESIGN CONTRACT (caP'n's law) ────────────────────────────────
// "Every holo we work on has to be better than the last." So this is NOT
// "one diamond, recolored." It's a RECIPE TABLE. Today, holo_jynxi is the
// fully-realized flagship (proven, lifted from the faction). Every other
// tier ships on a clean, coherent baseline and is a canvas: give a tier a
// richer recipe in HOLO_VISUALS and it levels up EVERYWHERE at once.
//
// ── MOTION SIGNATURES (the new axis) ─────────────────────────────────
// Color alone made every foil feel the same. So each recipe now names a
// `motion` — its animation personality, independent of color. MAGNUM
// FABLE pass (Jul 2026): every tier is now bespoke. No recolors remain.
//   "diamond"  — the classic single rotating conic shimmer. Kept as the
//                safe fallback geometry for unknown tiers.
//   "jester"   — FLAGSHIP J'ynxi: counter-rotating diamonds + glint wink
//                + the Cheshire grin. The green that grins back.
//   "void"     — the MAELSTROM (SVG): roiling fractal smoke ring around
//                a dark throat that eats the light.
//   "mercury"  — SILVER / QUICKSILVER: liquid-metal meniscus bands
//                settling down (and counter-drifting up) the card.
//   "molten"   — GOLD: a tongue of poured gold from the top seam +
//                twinkling flecks + the classic angle-catch sweep.
//   "prism"    — MYTHIC: chromatic aberration. R/G/B bands sweep at
//                different speeds, splitting and recombining into white.
//   "rend"     — SOLREND: THE SHATTERED MARK (caP'n's ruling, July 24):
//                a jagged seeded lightning-tear — the SAME polyline the
//                3D board paints (mulberry32 seed 0x50FA) — that tears
//                open, flares white-hot, and seals + embers + corona.
//   "halo"     — A'ETERNI: expanding rings of white light half a phase
//                apart (the pulse never ends) + twinkles + luminous breath.
//   "eclipse"  — SHADOW: a passing lobe of darkness (multiply — it EATS
//                light) while a violet rim-light circles the card edge.
//   "caustics" — DEPTHS (SVG): turbulence thresholded into a bright
//                cyan light-web (pool-floor caustics) + bubbles + tide.
//   "pillars"  — NIHIL (Swords of Nullus): standing columns of white
//                light with tiny black diamonds cascading down inside them.
//   "bullseye" — OMNIA (Blades of Omnis): concentric black/white target
//                reticles that surface, watch, and vanish across the card.
//   "grip"     — GAUNTLET (the Gauntlet's own, Aug 2026): four armoured
//                plates slide IN from the four edges, CLENCH at the
//                middle with a hard specular flash and an ember at the
//                knuckle, then release. The only tier that contracts —
//                every other one sweeps, spins, rises, falls or crosses.
//   "griddle"  — VROWN (Minerium One / Sir H'osh Vrown, Aug 2026): a
//                diagonal sear that PRESSES as it crosses, ramping
//                white-gold → amber → crisp umber-brown, carrying a
//                masked shred-grate that browns as the heat reaches
//                it. Sizzle sparks pop where it passed and a low amber
//                glow settles at the card's foot after — hash browns,
//                without being intrusive about it. One Minerium, one
//                holo; this one is never reissued.
// Adding a new motion = add a renderer in the component + its keyframes
// in KEYFRAMES, then point any tier's `motion` at it. Foils can share a
// color but never a motion.
//
// ── USAGE ────────────────────────────────────────────────────────────
//   // inside a position:relative card slot, AFTER the <img> art:
//   <HoloFoil tier="holo_jynxi" />
//
//   // tune intensity (dimmed locked previews vs. lit owned cards):
//   <HoloFoil tier={holoTier} intensity={owned ? 1 : 0.55} />
//
//   // match the art's corner radius so the overlay clips cleanly:
//   <HoloFoil tier="holo_mythic" radius={16} />
//
//   // corner flourishes are on by default; turn off for tiny tiles:
//   <HoloFoil tier="holo_gold" corners={false} />
//
// Pointer-events are off — it never eats clicks. It fills its positioned
// parent (inset:0). Render it INSIDE the clipped card container.
//
// ── GLOW (optional, separate) ────────────────────────────────────────
// The outer breathing box-shadow is a parent-level effect, so it's a
// helper, not part of the overlay. Spread it onto the card container:
//   <div style={{ ...holoGlowStyle("holo_jynxi") }}> … <HoloFoil …/> </div>
// ════════════════════════════════════════════════════════════════════

import React from "react";
// KHAO RENAME (July 25, 2026) — legacy tier keys still live in players'
// Firebase records. Resolving them HERE, at the one lookup, means a foil
// bought before the rename renders identically to one bought after.
// (PackArt imports nothing, so this cannot create a cycle.)
import { canonTier } from "./PackArt";

// ═══ THE SOLREND SHATTER — ONE BOLT, TWO RENDERERS (July 24) ═══════
// caP'n's C19 ruling: Solrend's face is the SHATTERED MARK the 3D board
// paints — the straight sweep-bar is retired. Coherence here is by
// CONSTRUCTION, not by eye: this is the same seeded polyline the native
// 3D texture painter builds (holoMaterial3D.js `texSolrend`) — same
// mulberry32, same seed 0x50FA, same 14 segments, same ±15%-width
// jitter. Change the seed in one file without the other and the Parity
// Bench will catch you; don't.
const SOLREND_SHATTER_PTS = (() => {
  let a = 0x50FA; // ⚠ MUST match holoMaterial3D texSolrend's seed forever
  const R = () => {
    a |= 0; a = (a + 0x6D2B79F5) | 0;
    let t = Math.imul(a ^ (a >>> 15), 1 | a);
    t = (t + Math.imul(t ^ (t >>> 7), 61 | t)) ^ t;
    return ((t ^ (t >>> 14)) >>> 0) / 4294967296;
  };
  const pts = [];
  // Native paints on 256-wide canvas: x = 0.5w + (R()-0.5)*0.30w.
  // Here in a 0..100 × 0..140 viewBox: x = 50 + (R()-0.5)*30.
  for (let i = 0; i <= 14; i++) pts.push([+(50 + (R() - 0.5) * 30).toFixed(2), +((i / 14) * 140).toFixed(2)]);
  return pts;
})();
const SOLREND_SHATTER_D =
  "M " + SOLREND_SHATTER_PTS.map((p) => p.join(" ")).join(" L ");

// ── HOLO_VISUALS — the recipe table (the single source of foil truth) ─
// Each tier maps to a recipe. Fields:
//   conic     : the rotating conic-gradient shimmer (the "diamond" pass).
//               An array of CSS color-stops; rendered from 0deg, rotating.
//   spin      : seconds per full shimmer rotation (lower = faster).
//   sweep     : true → add the diagonal catch-the-light glint band.
//   sweepMs   : seconds for one sweep pass.
//   glow      : base glow color (used by both the pulse ring and helper).
//   pulseMs   : seconds for the breathing glow pulse.
//   corners   : "gold" | "tier" | false — corner flourish accent.
//   blendShim : mix-blend-mode for the shimmer layer.
//   blendSwp  : mix-blend-mode for the sweep layer.
//   shimOp    : base opacity of the shimmer at intensity 1.
//   sweepOp   : base opacity of the sweep at intensity 1.
//
// holo_jynxi is the flagship — fully realized, lifted from the faction's
// proven `jesters-holo` look. Treat the others as live canvases: make any
// of them better than yesterday and it propagates everywhere.
export const HOLO_VISUALS = {
  // ── FLAGSHIP — fully realized (the look caP'n loves) ───────────────
  // The "jester" motion: a primary rotating diamond, a SECOND diamond
  // counter-rotating at a different speed, and a vertical liquid ripple
  // drifting over both — so the green never sits still. This is the
  // movement the faction card had that the flat single-spin lost.
  holo_jynxi: {
    motion: "jester",
    // The signature emerald shimmer. Gold accents kept SUBTLE so they
    // glint rather than flood — the old recipe's #facc15aa stop + flow
    // radials in color-dodge whited the whole card out. Lower alphas
    // here let the art read through while the green still sweeps.
    conic: [
      "transparent 0deg",
      "#22c55e33 50deg",
      "#86efac55 95deg",
      "#facc1544 130deg",   // gold accent, gentle
      "#22c55e33 175deg",
      "transparent 220deg",
      "#34d39944 270deg",
      "#a7f3d055 315deg",
      "transparent 360deg",
    ],
    // Second counter-rotating diamond — tighter + faster so the green
    // visibly churns instead of spinning rigidly. Kept low-alpha.
    conic2: [
      "transparent 0deg",
      "#4ade8044 70deg",
      "#bbf7d066 120deg",
      "transparent 185deg",
      "#34d39933 250deg",
      "transparent 320deg",
      "#86efac55 350deg",
      "transparent 360deg",
    ],
    spin: 6, spin2: 9,           // primary 6s CW; second 9s CCW
    sweep: true, sweepMs: 3.4,   // the traveling diagonal glint
    glow: "#22c55e", pulseMs: 2.8,
    corners: "gold",
    // soft-light for the shimmer so it tints rather than blows out;
    // overlay for the sweep keeps the highlight crisp but not white-hot.
    blendShim: "soft-light", blendSwp: "overlay",
    shimOp: 0.7, sweepOp: 0.55,
    shim2Op: 0.5,
    // ── GLINT STAR — the "wink." A single bright specular point that
    // arcs across the card on its own path. Restrained so it pops once
    // per cycle instead of adding a constant bright wash.
    glint: true, glintMs: 4.6, glintColor: "#fefce8",
    // ── GRIN — the emerald Cheshire crescent that surfaces and vanishes.
    // J'ynxi's exclusive motion signature: the green that grins back.
    grin: true, grinMs: 7, grinColor: "#bbf7d0",
    // NOTE: flow radials + second sweep + ripple intentionally REMOVED.
    // They stacked in color-dodge and drowned the card. The diamond +
    // sweep + wink + grin already give the full living-foil feel.
  },

  // ── BESPOKE TIERS — every foil has its own motion signature. ───────
  // MAGNUM FABLE pass (Jul 2026): no tier is a recolor of another. Each
  // recipe below names a unique motion; colors AND movement are its own.

  // ── SILVER — "QUICKSILVER". Liquid mercury settling. Two soft
  // meniscus bands undulate down (and counter-drift up) the card like
  // liquid metal finding its level. Understated — silver is the base
  // foil — but it visibly LIVES now instead of spinning a gray diamond.
  holo_silver: {
    motion: "mercury",
    noShimmer: true,
    conic: ["transparent 0deg", "#cccccc44 90deg", "#ffffff77 150deg", "#aaaaaa44 210deg", "transparent 320deg"],
    spin: 8, sweep: false, glow: "#cccccc", pulseMs: 4,
    corners: false, blendShim: "screen", shimOp: 0, sweepOp: 0,
    mercury: true,
    mercuryColor: "#e8eef4",     // cold specular silver
    mercuryColor2: "#9aa4b0",    // shaded meniscus underside
    mercuryMsA: 7.5,             // primary band drift (down)
    mercuryMsB: 11,              // counter band drift (up)
    mercuryOp: 0.5,
  },

  // ── GOLD — "MOLTEN". Poured gold. A warm tongue of light pours from
  // the top seam, stretching down and receding like gold leaf catching
  // candlelight, while tiny flecks twinkle in the surface. The classic
  // diagonal sweep stays — gold should still catch the angle.
  holo_gold: {
    motion: "molten",
    noShimmer: true,
    conic: ["transparent 0deg", "#b8860b55 70deg", "#ffd70099 130deg", "#daa52066 200deg", "transparent 330deg"],
    spin: 7, sweep: true, sweepMs: 4.2, glow: "#ffd700", pulseMs: 3.6,
    corners: "gold", blendShim: "screen", blendSwp: "overlay",
    shimOp: 0, sweepOp: 0.5,
    molten: true,
    moltenColor: "#ffd700",
    moltenColor2: "#b8860b",
    moltenMs: 6.5,               // one full pour-and-recede
    moltenOp: 0.55,
    flecks: true, fleckColor: "#fff3c4", fleckMs: 3.2, fleckOp: 0.85,
  },

  // ── MYTHIC — "PRISM". Chromatic aberration. Three thin spectral
  // bands — red, green, blue — sweep the card at slightly different
  // speeds, splitting apart and recombining into white where they cross:
  // genuine light-through-a-prism refraction, not a rainbow recolor.
  // A soft slow rainbow conic stays underneath as the full-spectrum bed.
  holo_mythic: {
    motion: "prism",
    conic: [
      "transparent 0deg", "#ff006633 50deg", "#ffcc0044 100deg",
      "#00ff8844 150deg", "#00ccff44 210deg", "#6600ff44 270deg",
      "#ff006633 330deg", "transparent 360deg",
    ],
    spin: 12, sweep: false, glow: "#ff66cc", pulseMs: 3,
    corners: "tier", blendShim: "soft-light",
    shimOp: 0.55, sweepOp: 0,
    prism: true,
    prismMs: 4.6,                // base band period; G/B run offset from it
    prismOp: 0.6,
  },
  // ── VOID — the bespoke standard-setter (the "MAELSTROM", SVG smoke) ─
  // CALIBRATION (caP'n, hard-won across many passes — READ before touching):
  //   • CSS gradient "smoke" FAILED repeatedly: a big soft blurred blob is
  //     (a) so smooth that rotating it shows NO visible motion, and (b) a
  //     flat even wash that BLOCKS the art. Both happened. Dead end.
  //   • The fix is REAL fractal noise via SVG feTurbulence — it has visible
  //     wispy STRUCTURE (so motion reads as roiling smoke) and we animate
  //     the noise itself (SMIL), not a rotating symmetric blob.
  //   • It must NOT block the art: the noise is masked to a TUNNEL (a ring
  //     of smoke around an open throat) and faded before the card edges, and
  //     kept translucent so the card reads through it.
  //   • Void = light eaten: a DARK throat (multiply) at the center; violet
  //     smoke, never a bright bloom.
  //   • Base diamond suppressed (noShimmer).
  // The look: genuine roiling violet smoke spiralling around a dark gullet —
  // a living maelstrom you can actually SEE move. Theme-true and legible.
  // ── KHAO — the bespoke standard-setter (the "MAELSTROM", SVG smoke) ─
  // RENAMED from holo_void, July 25, 2026 (caP'n's ruling). The recipe
  // below is byte-for-byte what it always was — the Maelstrom won, only
  // the name changed. Internal prop names still read `voidTurb*` /
  // `voidThroat*` on purpose: they are the MOTION ENGINE's vocabulary
  // (shared with the renderer further down this file), not the tier's
  // identity. Renaming them buys nothing and risks the artwork.
  holo_khao: {
    motion: "void",
    noShimmer: true,            // kill the rotating-square diamond entirely
    conic: ["transparent 0deg", "#6600cc44 120deg", "#9333ea66 240deg", "transparent 360deg"],
    spin: 9, sweep: false, glow: "#7c3aed", pulseMs: 4.4,
    corners: "tier", blendShim: "screen", shimOp: 0, sweepOp: 0,
    // SVG turbulence smoke maelstrom:
    voidTurb: true,
    voidTurbColor: "#9d5cff",   // smoke tint
    voidTurbColor2: "#5b21b6",  // deeper smoke tint
    voidTurbSpin: 26,           // slow rotation of the whole smoke ring (s)
    voidTurbOpacity: 0.55,      // overall smoke strength (translucency)
    voidThroat: true, voidThroatMs: 7, voidThroatColor: "#08000f", // dark gullet (multiply)
    voidThroatStrength: 0.62, // 0..1 darkness of the throat (lower = more art shows through)
  },
  // ── SOLREND — "THE REND". Sun-rend, literally: a fissure of sunfire
  // periodically TEARS open across the card — a thin blinding crimson
  // seam that blooms, flares white-hot at its heart, and seals — while
  // embers rise from the wound and a crimson corona breathes below.
  // Descent beyond rank: the card itself is being torn by the sun.
  holo_solrend: {
    motion: "rend",
    noShimmer: true,
    conic: ["transparent 0deg", "#ff000055 50deg", "#ff2266aa 110deg", "#cc002266 190deg", "transparent 320deg"],
    spin: 5, sweep: false, glow: "#ff2266", pulseMs: 3,
    corners: "tier", blendShim: "screen", shimOp: 0, sweepOp: 0,
    rend: true,
    rendColor: "#ff2266",
    rendCore: "#ffe9ec",         // the white-hot heart of the tear
    rendMs: 5.4,                 // tear-open → flare → seal cycle
    rendOp: 0.9,
    embers: true, emberColor: "#ff6a3d", emberMs: 4.2, emberOp: 0.8,
    corona: true, coronaColor: "#cc0022", coronaMs: 4.8, coronaOp: 0.4,
  },

  // ── A'ETERNI — "HALO". The heartbeat of eternity. Concentric rings
  // of white light expand slowly outward from the card's heart and
  // dissolve — two rings, offset, so the pulse never stops — while
  // faint stars twinkle and the whole surface breathes luminous.
  // Serene and absolute; nothing rushes, nothing repeats visibly.
  holo_aeternus: {
    motion: "halo",
    noShimmer: true,
    conic: ["transparent 0deg", "#ffffff55 80deg", "#ffffffaa 150deg", "#dddddd55 230deg", "transparent 340deg"],
    spin: 9, sweep: false, glow: "#f0f0ff", pulseMs: 5,
    corners: "gold", blendShim: "screen", shimOp: 0, sweepOp: 0,
    halo: true,
    haloColor: "#f0f0ff",
    haloMs: 6.8,                 // ring expansion period (2nd ring runs at half-phase)
    haloOp: 0.75,
    twinkles: true, twinkleColor: "#ffffff", twinkleMs: 4.4, twinkleOp: 0.9,
    breathe: true, breatheColor: "#e6e6ff", breatheMs: 7.5, breatheOp: 0.16,
  },

  // ── VROWN — "THE GRIDDLE". Minerium One's own signature, Aug 2026.
  // caP'n: "the very first Minerium belongs to Sir Hash Vrown [H'osh
  // Vrown]... mimic hash browns going white gold to brown without
  // being intrusive." So: a diagonal sear crosses the card ONCE per
  // cycle, ramping pale white-gold → amber → a crisp umber-brown edge
  // as it passes, then fades — never a static "brown card," always a
  // pass of heat. Sizzle sparks (the fleck engine, warm-toned) pop
  // where the sear just was; a low amber glow (the corona engine)
  // settles at the card's foot afterward, like a griddle cooling.
  // Every Mythic card earned in Minerium One wears this. It is his.
  holo_vrown: {
    motion: "griddle",
    noShimmer: true,
    conic: ["transparent 0deg", "#fdf6e355 70deg", "#c17a2e99 150deg", "#4a2b1266 230deg", "transparent 340deg"],
    spin: 7.5, sweep: false, glow: "#c17a2e", pulseMs: 4,
    corners: "gold", blendShim: "overlay", shimOp: 0, sweepOp: 0,
    griddle: true,
    griddleColorA: "#fdf6e3",    // white-gold — the sear's leading edge
    griddleColorB: "#c17a2e",    // amber — the sear's core
    griddleColorC: "#3a2210",    // crisp umber-brown — the trailing crust
    griddleMs: 6.2,              // one full pass, edge to edge
    griddleOp: 0.62,
    // ⭐ THE SHREDS. caP'n's actual image: "mimic mini hash browns
    // going white gold to brown without being intrusive." The sear
    // alone was a band of heat with no potato in it. This is the
    // grate: fine diagonal shreds that travel INSIDE the band, masked
    // so they are invisible at the white-gold leading edge and crisp
    // by the umber trailing crust — so what you watch is a shred
    // BROWNING, not a brown stripe sliding past. "Without being
    // intrusive" is the brief and it is why the pitch is 6px and the
    // opacity is a third of the sear's: at card size it reads as
    // texture, and you only catch what it is if you look.
    shreds: true,
    shredColor: "#5a3312",
    shredOp: 0.22,
    flecks: true, fleckColor: "#ffdca0", fleckMs: 2.6, fleckOp: 0.8,   // sizzle
    corona: true, coronaColor: "#7a4419", coronaMs: 5.5, coronaOp: 0.32, // cooling glow
  },

  // ── GAUNTLET — "THE GRIP". The Gauntlet's own signature, Aug 2026.
  // caP'n asked for the mode to have its own mark and its own foil the
  // way Minerium One has the Griddle, and left the design to V'ael.
  //
  // EVERY OTHER TIER MOVES OUTWARD OR ACROSS. Shimmer rotates, sweep
  // and glint travel, halo expands, pillars rise, motes fall, the sear
  // crosses. Not one of them CLOSES. So the Gauntlet does: four
  // armoured plates slide IN from the four edges, meet at the middle
  // of the card, CLENCH — a hard specular flash at the instant of
  // contact and a compression that lasts about a fifth of a second —
  // and then release back out. Contraction is the one gesture the tier
  // list had left, which is exactly why it belongs to the mode that is
  // about being held to a standard until you break or you don't.
  //
  // Steel that has been used, not steel from a shelf: cold plate with
  // an ember at the knuckle, because the thing gripping you has been
  // working.
  holo_gauntlet: {
    motion: "grip",
    noShimmer: true,
    conic: ["transparent 0deg", "#8e9aa666 60deg", "#dfe7efaa 140deg", "#5a636c66 220deg", "transparent 330deg"],
    spin: 8, sweep: false, glow: "#b9c2cc", pulseMs: 3.4,
    corners: "tier", blendShim: "overlay", shimOp: 0, sweepOp: 0,
    grip: true,
    gripPlate: "#c6cfd9",        // the cold face of the plate
    gripEdge: "#f2f7fb",         // the lit edge that catches as it moves
    gripEmber: "#ff6a2a",        // the knuckle, and the rivets
    gripMs: 4.4,                 // in, clench, out — one full close
    gripOp: 0.55,
    // The instant of contact. Its own layer because a flash that lives
    // inside the plate keyframes cannot be tuned without moving them.
    clench: true, clenchColor: "#ffffff", clenchOp: 0.5,
  },

  // ── SHADOW — RETIRED July 25, 2026 (caP'n's ruling) ────────────────
  // Read too close to Void both visually and lexically (IE_HOLO_DOCTRINE
  // §11). Void won the artwork and became **Khao**; Shadow leaves the
  // shelf until a genuinely distinct Shadow variant is cut.
  //
  // The recipe is preserved verbatim below rather than deleted, and the
  // "eclipse" motion engine further down this file is LEFT INTACT — the
  // next Shadow will almost certainly want a passing lobe of darkness,
  // and re-deriving it costs a session. Uncomment, retune, ship.
  //
  // ⚠ DOCTRINE §5: while this is commented out, any collection still
  // holding holo_shadow renders BASE + flag. It must NEVER be aliased
  // onto Khao or any other tier to "fix" the gap.
  //
  // holo_shadow: {
  //   motion: "eclipse",
  //   noShimmer: true,
  //   conic: ["transparent 0deg", "#1a003566 70deg", "#2d0066aa 140deg", "#6600cc66 220deg", "transparent 340deg"],
  //   spin: 8, sweep: false, glow: "#6600cc", pulseMs: 4.5,
  //   corners: "tier", blendShim: "multiply", shimOp: 0, sweepOp: 0,
  //   eclipse: true,
  //   eclipseColor: "#0a0018",     // the passing dark
  //   eclipseMs: 8.5,
  //   eclipseOp: 0.7,
  //   rim: true, rimColor: "#9966ff", rimMs: 8.5, rimOp: 0.75,
  // },

  // ── DEPTHS — "CAUSTICS". The Gatekeeper's signature, and the second
  // SVG-noise foil (a completely different config from Void's smoke):
  // REAL underwater light-webbing. feTurbulence is thresholded into
  // thin bright cyan filaments — the dancing net of light on a pool
  // floor — drifting slowly upward as if the surface is far overhead,
  // while bubbles rise and a deep-blue tide breathes from below.
  holo_depths: {
    motion: "caustics",
    noShimmer: true,
    conic: ["transparent 0deg", "#001a3366 60deg", "#00aaffaa 130deg", "#00448866 210deg", "transparent 330deg"],
    spin: 6.5, sweep: false, glow: "#00aaff", pulseMs: 3.8,
    corners: "tier", blendShim: "screen", shimOp: 0, sweepOp: 0,
    caustics: true,
    causticColor: "#7fd8ff",     // the light-web tint
    causticDriftMs: 13,          // slow upward drift of the whole web
    causticOp: 0.5,
    bubbles: true, bubbleColor: "#bfeaff", bubbleMs: 6.5, bubbleOp: 0.7,
    tide: true, tideColor: "#003a66", tideMs: 7.2, tideOp: 0.45,
  },

  // ── NIHIL (Swords of Nullus) — "PILLARS". The faction of the void made
  // foil: tall standing pillars of cold white light, and WITHIN them a
  // cascade of tiny black diamonds falling endlessly downward — presence
  // and absence, the nothing pouring through the something. The pillars
  // breathe (screen — they GLOW); the diamonds fall (multiply — they EAT
  // light), each a hairline-rotated void mote streaming down its column.
  // A completely different motion from every other tier: no rotation, no
  // ring, no horizontal sweep — vertical light with dark rain inside it.
  holo_nihil: {
    motion: "pillars",
    noShimmer: true,
    conic: ["transparent 0deg", "#e8e8f044 90deg", "#ffffff66 150deg", "#c8c8d044 210deg", "transparent 320deg"],
    spin: 9, sweep: false, glow: "#e8e8f0", pulseMs: 4.2,
    corners: "gold", blendShim: "screen", shimOp: 0, sweepOp: 0,
    pillars: true,
    pillarColor: "#f4f4f8",      // the cold white column light
    pillarMs: 4.6,               // gentle column breathe
    pillarOp: 0.5,
    motes: true,                 // the cascading black diamonds
    moteColor: "#08080e",        // near-void black
    moteMs: 3.8,                 // one full top→bottom fall
    moteOp: 0.85,
  },

  // ── OMNIA (Blades of Omnis) — "BULLSEYE". The all-cataloguing eye made
  // foil: concentric black-and-white rings — target reticles — that bloom
  // into being at scattered points across the card, hold, and dissolve,
  // over and over, never in sync. Omnis collects everything; the card is
  // watched from a dozen surfacing eyes. Rings alternate black/white so
  // each reads as a true bullseye; they SURFACE (scale-in) and VANISH
  // (scale-out + fade). No sweep, no spin — a field of blinking targets.
  holo_omnia: {
    motion: "bullseye",
    noShimmer: true,
    conic: ["transparent 0deg", "#1a1a2288 90deg", "#f0f0f455 150deg", "#0a0a1088 210deg", "transparent 320deg"],
    spin: 10, sweep: false, glow: "#cfc6ff", pulseMs: 4.5,
    corners: "tier", blendShim: "multiply", shimOp: 0, sweepOp: 0,
    bullseye: true,
    ringDark: "#08080e",         // the dark rings of the reticle
    ringLight: "#f0f0f4",        // the light rings between them
    bullseyeMs: 4.4,             // surface → hold → vanish cycle
    bullseyeOp: 0.85,
  },
};

// Anything not in the table falls back to a safe, neutral silver recipe
// so an unknown/new tier never renders blank.
const FALLBACK = HOLO_VISUALS.holo_silver;
export const getHoloVisual = (tier) =>
  HOLO_VISUALS[canonTier(tier)] || HOLO_VISUALS[tier] || FALLBACK;

// ── Shared keyframes — injected once per page, regardless of how many
// HoloFoils mount. Class names are namespaced `hf-` so they can NEVER
// collide with the legacy `jesters-holo-*`, `feat-foil-*`, `holo-foil-*`,
// `cx-*`, `sf-*`, `cv-*` rules still living on other screens this deploy
// does not touch.
const KEYFRAMES = `
@keyframes hf-spin    { 0% { transform: rotate(0deg); }   100% { transform: rotate(360deg); } }
@keyframes hf-spin-cc { 0% { transform: rotate(360deg); } 100% { transform: rotate(0deg); } }
@keyframes hf-sweep  {
  0%   { transform: translateX(-130%) skewX(-20deg); }
  55%  { transform: translateX(130%)  skewX(-20deg); }
  100% { transform: translateX(130%)  skewX(-20deg); }
}
@keyframes hf-ripple {
  0%   { transform: translateY(-12%) scale(1.05); opacity: 0.0; }
  35%  { opacity: 1; }
  100% { transform: translateY(12%)  scale(1.12); opacity: 0.0; }
}
/* ── FLOW PASS (jester motion) ───────────────────────────────────
   Two large soft radials of emerald+gold that POOL and TRAVEL across
   the card on different paths/speeds, breathing their scale so the
   light looks like it's refracting over the foil — the churn the
   faction card had. They never sync, so the surface never sits still. */
@keyframes hf-flow-a {
  0%   { transform: translate(-18%, -12%) scale(1.0)  rotate(0deg);   }
  25%  { transform: translate(12%,  8%)   scale(1.25) rotate(4deg);   }
  50%  { transform: translate(18%,  16%)  scale(1.1)  rotate(0deg);   }
  75%  { transform: translate(-6%,  6%)   scale(1.3)  rotate(-4deg);  }
  100% { transform: translate(-18%, -12%) scale(1.0)  rotate(0deg);   }
}
@keyframes hf-flow-b {
  0%   { transform: translate(16%,  14%)  scale(1.2)  rotate(0deg);   }
  30%  { transform: translate(-10%, -8%)  scale(1.0)  rotate(-5deg);  }
  60%  { transform: translate(-16%, 10%)  scale(1.3)  rotate(3deg);   }
  100% { transform: translate(16%,  14%)  scale(1.2)  rotate(0deg);   }
}
/* Second sweep — wider, slower wash that trails the primary glint. */
@keyframes hf-sweep2 {
  0%   { transform: translateX(-150%) skewX(-14deg); }
  60%  { transform: translateX(150%)  skewX(-14deg); }
  100% { transform: translateX(150%)  skewX(-14deg); }
}
/* ── GLINT STAR (jester "wink") ───────────────────────────────────
   A single bright specular point that arcs across the card on its own
   curved path — diagonal travel with a vertical bow — flaring bright at
   the apex then fading. This is the trickster's catch of light that the
   spinning diamonds alone could never give. Its keyframe was missing,
   so the wink sat frozen in the top-left corner; this brings it back. */
@keyframes hf-glint {
  0%   { transform: translate(-30%, 10%)  scale(0.6); opacity: 0; }
  15%  { opacity: 0.9; }
  45%  { transform: translate(150%, 70%)  scale(1.15); opacity: 1; }
  55%  { transform: translate(230%, 95%)  scale(0.7); opacity: 0; }
  100% { transform: translate(230%, 95%)  scale(0.6); opacity: 0; }
}
/* ── GRIN (jester's exclusive signature) ──────────────────────────
   A bright emerald crescent that rises through the foil, tilts, flares,
   and vanishes like a Cheshire smile surfacing — the one motion no other
   green foil shares: J'ynxi literally grins back. Keyframe was missing,
   so the crescent rendered invisible (opacity 0) and never animated. */
@keyframes hf-grin {
  0%   { transform: translateY(40%)  rotate(-12deg) scaleX(0.7); opacity: 0; }
  30%  { transform: translateY(0%)   rotate(-4deg)  scaleX(1.0); opacity: 0.85; }
  50%  { transform: translateY(-6%)  rotate(3deg)   scaleX(1.08); opacity: 0.95; }
  70%  { transform: translateY(-2%)  rotate(8deg)   scaleX(1.0); opacity: 0.7; }
  100% { transform: translateY(-30%) rotate(14deg)  scaleX(0.7); opacity: 0; }
}
/* ── VOID (holo_void's exclusive signature — SVG "MAELSTROM") ──────
   The smoke is REAL fractal noise (SVG feTurbulence), so it has visible
   wispy structure and the roiling is animated INSIDE the SVG (SMIL on the
   turbulence). Out here we only:
   • hf-void-ring — slowly ROTATE the whole smoke disc so the maelstrom
     turns (the roil + the rotation together read as living smoke).
   • hf-void-throat — pulse the dark gullet that eats the light (multiply). */
@keyframes hf-void-ring {
  0%   { transform: translate(-50%, -50%) rotate(0deg); }
  100% { transform: translate(-50%, -50%) rotate(360deg); }
}
@keyframes hf-void-throat {
  0%   { transform: translate(-50%, -50%) scale(0.92); opacity: 0.78; }
  50%  { transform: translate(-50%, -50%) scale(1.12); opacity: 0.95; }
  100% { transform: translate(-50%, -50%) scale(0.92); opacity: 0.78; }
}
/* ── QUICKSILVER (silver) — liquid mercury settling ────────────────
   Two soft meniscus bands: the primary undulates DOWN the card with a
   gentle skew wobble (liquid finding its level); the counter band
   drifts UP slower. They never sync — the metal never sits still. */
@keyframes hf-mercury-a {
  0%   { transform: translateY(-70%) skewY(1.5deg)  scaleY(1);    opacity: 0; }
  18%  { opacity: 1; }
  50%  { transform: translateY(20%)  skewY(-1.2deg) scaleY(1.15); opacity: 0.9; }
  82%  { opacity: 1; }
  100% { transform: translateY(110%) skewY(1.5deg)  scaleY(1);    opacity: 0; }
}
@keyframes hf-mercury-b {
  0%   { transform: translateY(110%) skewY(-1deg)  scaleY(1.1); opacity: 0; }
  22%  { opacity: 0.8; }
  55%  { transform: translateY(10%)  skewY(1.3deg) scaleY(0.95); opacity: 0.7; }
  100% { transform: translateY(-80%) skewY(-1deg)  scaleY(1.1); opacity: 0; }
}
/* ── MOLTEN (gold) — the pour ──────────────────────────────────────
   A tongue of gold pours from the top seam, stretches down, hangs a
   beat at full reach, then recedes. Flecks twinkle independently. */
@keyframes hf-molten {
  0%   { transform: scaleY(0.12); opacity: 0; }
  18%  { transform: scaleY(0.55); opacity: 0.95; }
  46%  { transform: scaleY(1.0);  opacity: 1; }
  60%  { transform: scaleY(0.96); opacity: 0.9; }
  100% { transform: scaleY(0.1);  opacity: 0; }
}
@keyframes hf-fleck {
  0%, 100% { opacity: 0;   transform: scale(0.5); }
  50%      { opacity: 1;   transform: scale(1.2); }
}
/* ── PRISM (mythic) — chromatic aberration ─────────────────────────
   One band keyframe; the R/G/B copies run it at DIFFERENT durations
   (set inline), so the channels split apart and recombine into white
   where they cross — real refraction, never a static rainbow. */
@keyframes hf-prism {
  0%   { transform: translateX(-140%) skewX(-18deg); }
  60%  { transform: translateX(140%)  skewX(-18deg); }
  100% { transform: translateX(140%)  skewX(-18deg); }
}
/* ── THE REND (solrend) — the sunfire fissure ──────────────────────
   The tear: a thin diagonal seam blooms open (scaleY from a hairline),
   flares white-hot, and seals. Embers rise from it; the corona breathes. */
@keyframes hf-rend {
  0%   { transform: rotate(-24deg) scaleY(0.02) scaleX(0.6); opacity: 0; }
  12%  { transform: rotate(-24deg) scaleY(0.5)  scaleX(0.9); opacity: 0.9; }
  26%  { transform: rotate(-24deg) scaleY(1.0)  scaleX(1.0); opacity: 1; }
  38%  { transform: rotate(-24deg) scaleY(0.7)  scaleX(1.0); opacity: 0.85; }
  55%  { transform: rotate(-24deg) scaleY(0.06) scaleX(0.7); opacity: 0.25; }
  100% { transform: rotate(-24deg) scaleY(0.02) scaleX(0.6); opacity: 0; }
}
/* THE SHATTERED MARK (July 24) — the bolt's event envelope. Mirrors the
   native 3D motion's character: DARK most of the cycle, then tear-open,
   a brief white-hot FLARE (brightness spike), and a seal back to dark.
   Drives the whole SVG bolt wrapper; per-stroke alphas are static. */
@keyframes hf-rendShatter {
  0%, 50% { opacity: 0;    transform: scaleX(0.6); filter: brightness(1); }
  58%     { opacity: 0.85; transform: scaleX(0.94); filter: brightness(1); }
  66%     { opacity: 1;    transform: scaleX(1);    filter: brightness(1.75); }
  74%     { opacity: 0.85; transform: scaleX(1);    filter: brightness(1); }
  100%    { opacity: 0;    transform: scaleX(0.7);  filter: brightness(1); }
}
@keyframes hf-ember {
  0%   { transform: translateY(30%)  translateX(0)    scale(0.6); opacity: 0; }
  15%  { opacity: 1; }
  60%  { transform: translateY(-40%) translateX(8%)   scale(1);   opacity: 0.8; }
  100% { transform: translateY(-95%) translateX(-5%)  scale(0.4); opacity: 0; }
}
@keyframes hf-corona {
  0%, 100% { opacity: 0.55; transform: scaleY(0.9); }
  50%      { opacity: 1;    transform: scaleY(1.15); }
}
/* ── HALO (a'eterni) — the heartbeat of eternity ──────────────────
   A ring of white light expands from the heart and dissolves. Two
   rings run half a phase apart (inline delay) so the pulse is eternal. */
@keyframes hf-halo {
  0%   { transform: translate(-50%, -50%) scale(0.15); opacity: 0; }
  12%  { opacity: 0.9; }
  70%  { opacity: 0.35; }
  100% { transform: translate(-50%, -50%) scale(1.45); opacity: 0; }
}
@keyframes hf-twinkle {
  0%, 100% { opacity: 0;   transform: scale(0.4) rotate(0deg); }
  50%      { opacity: 1;   transform: scale(1)   rotate(45deg); }
}
@keyframes hf-breathe {
  0%, 100% { opacity: 0.4; }
  50%      { opacity: 1; }
}
/* ── ECLIPSE (shadow) — the passing dark ──────────────────────────
   A lobe of darkness crosses the card like a body before a sun. Its
   rim-light circles the edge in step (same duration), flaring as the
   dark passes center. Shadow REMOVES light; every other foil adds it. */
@keyframes hf-eclipse {
  0%   { transform: translateX(-120%) translateY(-8%); opacity: 0; }
  20%  { opacity: 1; }
  50%  { transform: translateX(0%)    translateY(4%);  opacity: 1; }
  80%  { opacity: 1; }
  100% { transform: translateX(120%)  translateY(-8%); opacity: 0; }
}
@keyframes hf-rim {
  0%   { transform: rotate(0deg);   opacity: 0.4; }
  50%  { transform: rotate(180deg); opacity: 1; }
  100% { transform: rotate(360deg); opacity: 0.4; }
}
/* ── CAUSTICS (depths) — underwater light-web ─────────────────────
   The SVG web itself churns via SMIL (inside the filter). Out here we
   only DRIFT the whole web slowly upward — the surface is far overhead
   — and float the bubbles. The tide breathes at the card's foot. */
@keyframes hf-caustic-drift {
  0%   { transform: translateY(6%); }
  100% { transform: translateY(-6%); }
}
@keyframes hf-bubble {
  0%   { transform: translateY(15%)  translateX(0)   scale(0.5); opacity: 0; }
  12%  { opacity: 0.9; }
  55%  { transform: translateY(-45%) translateX(6%)  scale(0.9); opacity: 0.7; }
  100% { transform: translateY(-105%) translateX(-4%) scale(1.1); opacity: 0; }
}
@keyframes hf-tide {
  0%, 100% { opacity: 0.5; transform: translateY(6%); }
  50%      { opacity: 1;   transform: translateY(0%); }
}
/* ── PILLARS (nihil) — standing white columns + falling black motes ──
   The columns don't move; they only BREATHE (opacity + a hair of scaleY)
   so they read as living light. The motes FALL: each tiny diamond streams
   from above the column to below it, fading in at the top lip and out at
   the foot — a cascade of void through the light. Rotation is baked into
   the fall keyframe (45deg held) so every mote stays a diamond, not a
   square, for the whole descent. */
@keyframes hf-nihil-pillar {
  0%, 100% { opacity: 0.35; transform: scaleY(0.98); }
  50%      { opacity: 1;    transform: scaleY(1.03); }
}
@keyframes hf-nihil-fall {
  0%   { top: -18%; transform: translateX(-50%) rotate(45deg); opacity: 0; }
  12%  { opacity: 0.95; }
  85%  { opacity: 0.9; }
  100% { top: 118%; transform: translateX(-50%) rotate(45deg); opacity: 0; }
}
/* ── BULLSEYE (omnia) — surfacing target reticles ────────────────────
   Each bullseye blooms from nothing (scale 0 → 1), holds while fully
   present, then keeps expanding slightly as it fades (scale → 1.18,
   opacity → 0): a target that opens its eye, watches, and closes. Per
   -reticle delays (inline) keep the field forever out of sync. */
@keyframes hf-omnia-pop {
  0%   { transform: translate(-50%, -50%) scale(0);    opacity: 0; }
  22%  { transform: translate(-50%, -50%) scale(1);    opacity: 1; }
  62%  { transform: translate(-50%, -50%) scale(1.02); opacity: 1; }
  100% { transform: translate(-50%, -50%) scale(1.18); opacity: 0; }
}
/* ── THE GRIP (gauntlet) — the Gauntlet's own, Aug 2026 ────────────
   Four plates, four keyframes, because a CSS keyframe cannot be given
   a direction as a parameter and faking it with one shared animation
   plus per-element rotation would tilt the plates' gradients too. The
   shape is identical in all four: travel in over the first 40%, HOLD
   and compress through 44-58% (that dwell is the clench and it is what
   makes the gesture read as a grip rather than a shutter), then out.
   The card is never fully covered — each plate stops short of centre,
   so what closes is a frame, not a lid. */
@keyframes hf-grip-t {
  0%   { transform: translateY(-115%); opacity: 0; }
  16%  { opacity: 1; }
  44%  { transform: translateY(-6%);   opacity: 1; }
  58%  { transform: translateY(-2%) scaleY(1.1); opacity: 1; }
  84%  { opacity: 1; }
  100% { transform: translateY(-115%); opacity: 0; }
}
@keyframes hf-grip-b {
  0%   { transform: translateY(115%);  opacity: 0; }
  16%  { opacity: 1; }
  44%  { transform: translateY(6%);    opacity: 1; }
  58%  { transform: translateY(2%) scaleY(1.1);  opacity: 1; }
  84%  { opacity: 1; }
  100% { transform: translateY(115%);  opacity: 0; }
}
@keyframes hf-grip-l {
  0%   { transform: translateX(-125%); opacity: 0; }
  16%  { opacity: 1; }
  44%  { transform: translateX(-8%);   opacity: 1; }
  58%  { transform: translateX(-3%) scaleX(1.1); opacity: 1; }
  84%  { opacity: 1; }
  100% { transform: translateX(-125%); opacity: 0; }
}
@keyframes hf-grip-r {
  0%   { transform: translateX(125%);  opacity: 0; }
  16%  { opacity: 1; }
  44%  { transform: translateX(8%);    opacity: 1; }
  58%  { transform: translateX(3%) scaleX(1.1);  opacity: 1; }
  84%  { opacity: 1; }
  100% { transform: translateX(125%);  opacity: 0; }
}
/* The instant of contact — a hard, short flash and an ember at the
   knuckle. Nothing else on the card is allowed to be this abrupt. */
@keyframes hf-clench {
  0%,40%  { opacity: 0; transform: scale(0.86); }
  47%     { opacity: 1; transform: scale(1.04); }
  56%     { opacity: 0.42; transform: scale(1); }
  70%,100%{ opacity: 0; transform: scale(0.9); }
}
/* ── THE GRIDDLE (vrown) — Sir H'osh Vrown's own, Minerium One ────
   Not a plain sweep: the band fades in, crosses to center, then
   PRESSES — widening (scaleX) and dwelling through 42%-58% before
   continuing off the far edge, like a spatula bearing down mid-pass.
   That press-and-widen is the shape no other tier's sweep/glint uses.
   The colour RAMP (white-gold to umber-brown) is baked into the
   gradient stops in the renderer, not the keyframe — this only ever
   moves and shapes the band. */
@keyframes hf-griddle {
  0%   { transform: translateX(-135%) translateY(-2%) skewX(-13deg) scaleX(1);    opacity: 0; }
  14%  { opacity: 1; }
  42%  { transform: translateX(-9%)   translateY(0%)   skewX(-13deg) scaleX(1.08); opacity: 1; }
  58%  { transform: translateX(9%)    translateY(1%)   skewX(-13deg) scaleX(1.14); opacity: 1; }
  86%  { opacity: 1; }
  100% { transform: translateX(135%)  translateY(-2%)  skewX(-13deg) scaleX(1);    opacity: 0; }
}
@media (prefers-reduced-motion: reduce) {
  .hf-shimmer, .hf-shimmer2, .hf-sweep, .hf-sweep2, .hf-ripple,
  .hf-flow-a, .hf-flow-b, .hf-glint, .hf-grin,
  .hf-void-ring, .hf-void-throat,
  .hf-mercury-a, .hf-mercury-b, .hf-molten, .hf-fleck, .hf-prism,
  .hf-rend, .hf-ember, .hf-corona, .hf-halo, .hf-twinkle, .hf-breathe,
  .hf-eclipse, .hf-rim, .hf-caustic, .hf-bubble, .hf-tide,
  .hf-nihil-pillar, .hf-nihil-mote, .hf-omnia, .hf-griddle,
  .hf-grip, .hf-clench { animation: none !important; }
  /* THE GRIDDLE AT REST. Killing the animation alone parks BOTH griddle
     layers at their 0% transform, which is off the left edge — fine for
     the sear, but the shred grate would then sit as a static brown
     hatch if it ever resolved differently. It is hidden outright, and
     the sear is dimmed to a still warm wash rather than a frozen bar:
     a reduced-motion player gets the tier's COLOUR, not a stopped
     frame of its motion. */
  .hf-griddle-shred { opacity: 0 !important; }
  /* THE SHATTERED MARK — its keyframes own the visible opacity (inline
     floor is 0), so killing the animation alone would ERASE the foil for
     reduced-motion players. It rests instead as a still, lit bolt. */
  .hf-rend { opacity: 0.75 !important; transform: none !important; }
}
`;

// ── Keyframe injection — lifecycle-proof. ─────────────────────────
// PITFALL WE HIT: the old guard rendered the <style> from inside the
// FIRST <HoloFoil> and flipped a module boolean. When that first foil
// later unmounted (scrolled away / screen change), React pulled its
// <style> out of the DOM — but the boolean stayed true, so EVERY later
// foil returned null and the keyframes were simply gone. Foils rendered
// but nothing animated (the static gold sheet). Fix: inject the rules
// straight into <head> once, keyed off the DOM node actually existing,
// so they persist no matter which component mounts or unmounts.
const HF_STYLE_ID = "hf-keyframes";
function ensureKeyframes() {
  if (typeof document === "undefined") return;          // SSR-safe
  // ── THE STALE-SHEET FIX (July 25) ─────────────────────────────────
  // The old guard skipped injection whenever ANY element with this id
  // existed — including one injected by a PREVIOUS version of this file
  // (hot reload, in-place deploy, long-lived tab). That is exactly how
  // Solrend's shattered mark went invisible: hf-rendShatter was added to
  // KEYFRAMES on July 24, the stale sheet in <head> predated it, the
  // animation name never resolved, and the bolt wrapper sat at its
  // inline opacity floor of 0 — "just the red aura." Now the guard
  // compares CONTENT: if the mounted sheet isn't this build's CSS,
  // it is replaced. Every future recipe edit self-heals the same way.
  const existing = document.getElementById(HF_STYLE_ID);
  if (existing) {
    if (existing.textContent !== KEYFRAMES) existing.textContent = KEYFRAMES;
    return;
  }
  const el = document.createElement("style");
  el.id = HF_STYLE_ID;
  el.textContent = KEYFRAMES;
  document.head.appendChild(el);
}
// Inject at module load so the rules exist before any foil paints.
ensureKeyframes();

// Kept as a no-op component so existing <HoloKeyframes /> mounts in the
// render tree don't break; the real work is done by ensureKeyframes().
function HoloKeyframes() {
  ensureKeyframes();
  return null;
}

// ── The breathing outer glow — a PARENT effect, exposed as a helper.
// Spread onto the card container's style. Returns an object with an inline
// animation so callers don't need to manage keyframes (uses box-shadow
// transition rather than a keyframe to stay self-contained).
export function holoGlowStyle(tier) {
  ensureGlowKeyframes(tier); // self-inject so the animation name resolves
  const v = getHoloVisual(tier);
  const g = v.glow;
  return {
    boxShadow: `0 0 18px ${g}aa, 0 0 36px ${g}55, inset 0 0 10px rgba(0,0,0,0.5)`,
    // a gentle, dependency-free breathe via CSS animation name we define:
    animation: `hf-glow-${slug(tier)} ${v.pulseMs}s ease-in-out infinite`,
  };
}

// Per-tier glow keyframes — same lifecycle-proof <head> injection as the
// shared block (the Set-guarded React <style> had the same unmount bug).
function ensureGlowKeyframes(tier) {
  if (typeof document === "undefined") return;
  const key = slug(tier);
  const id = `hf-glow-${key}`;
  const v = getHoloVisual(tier);
  const g = v.glow;
  const css = `
    @keyframes hf-glow-${key} {
      0%,100% { box-shadow: 0 0 18px ${g}aa, 0 0 36px ${g}55, inset 0 0 10px rgba(0,0,0,0.5); }
      50%     { box-shadow: 0 0 28px ${g}cc, 0 0 56px ${g}77, inset 0 0 10px rgba(0,0,0,0.5); }
    }`;
  // Stale-sheet fix (same as ensureKeyframes): replace on content drift.
  const existing = document.getElementById(id);
  if (existing) { if (existing.textContent !== css) existing.textContent = css; return; }
  const el = document.createElement("style");
  el.id = id;
  el.textContent = css;
  document.head.appendChild(el);
}
function GlowKeyframes({ tier }) {
  ensureGlowKeyframes(tier);
  return null;
}

function slug(tier) {
  return String(tier || "silver").replace(/[^a-z0-9]/gi, "");
}

// ── HoloFoil — the overlay. Render inside a clipped, position:relative
// card container, after the art <img>.
//
//   tier       holo tier id (e.g. "holo_jynxi"). Unknown → silver fallback.
//   intensity  0..1 multiplier on shimmer + sweep opacity (default 1).
//   radius     border-radius (px) to match the card art clip (default 10).
//   corners    override the recipe's corner flourish (true/false).
//   className  extra class on the root, if a caller wants to hook it.
export default function HoloFoil({
  tier = "holo_silver",
  intensity = 1,
  radius = 10,
  corners,
  className = "",
  style = {},
}) {
  const v = getHoloVisual(tier);
  const showCorners = corners !== undefined ? corners : !!v.corners;
  const cornerColor = v.corners === "tier" ? v.glow : "#e8b84b"; // gold default

  const shimOpacity = Math.max(0, Math.min(1, v.shimOp * intensity));
  const sweepOpacity = Math.max(0, Math.min(1, (v.sweepOp || 0) * intensity));

  // Unique SVG ids per mount so multiple foils never share (and thus corrupt)
  // each other's filter/gradient. useId can contain ":" which is invalid in
  // url(#…) refs, so strip non-word chars.
  const _uid = React.useId().replace(/[^a-zA-Z0-9_-]/g, "");
  const voidFilterId = `hf-void-turb-${_uid}`;
  const voidGradId = `hf-void-grad-${_uid}`;
  const causticFilterId = `hf-caustic-${_uid}`;

  return (
    <div
      className={`hf-root ${className}`}
      aria-hidden="true"
      style={{
        position: "absolute",
        inset: 0,
        borderRadius: radius,
        overflow: "hidden",
        pointerEvents: "none",
        ...style,
      }}
    >
      <HoloKeyframes />

      {/* Rotating conic "diamond" shimmer — the signature pass. Oversized
          and centered so the rotating square corners never reveal an edge.
          SUPPRESSED when a recipe sets noShimmer (curved-motion tiers like
          Void replace this straight-edged diamond with their own geometry —
          the rotating square is exactly what fights a spiral theme). */}
      {!v.noShimmer && (
        <div
          className="hf-shimmer"
          style={{
            position: "absolute",
            // 150% box centered keeps the conic field covering the slot
            // through a full rotation without clipping artifacts.
            top: "-25%", left: "-25%", width: "150%", height: "150%",
            background: `conic-gradient(from 0deg, ${v.conic.join(", ")})`,
            animation: `hf-spin ${v.spin}s linear infinite`,
            mixBlendMode: v.blendShim || "color-dodge",
            opacity: shimOpacity,
          }}
        />
      )}

      {/* SECOND counter-rotating diamond — only when the recipe defines a
          conic2 (jester motion). A different speed + reverse direction is
          what makes the green visibly churn instead of just spin. */}
      {v.conic2 && (
        <div
          className="hf-shimmer2"
          style={{
            position: "absolute",
            top: "-30%", left: "-30%", width: "160%", height: "160%",
            background: `conic-gradient(from 90deg, ${v.conic2.join(", ")})`,
            animation: `hf-spin-cc ${v.spin2 || (v.spin * 1.5)}s linear infinite`,
            mixBlendMode: v.blendShim || "color-dodge",
            opacity: Math.max(0, Math.min(1, (v.shim2Op || 0) * intensity)),
          }}
        />
      )}

      {/* Vertical liquid ripple — a soft band drifting top→bottom, giving
          the foil a flowing, breathing quality over the rotation. */}
      {v.ripple && (
        <div
          className="hf-ripple"
          style={{
            position: "absolute",
            left: 0, right: 0, height: "60%", top: "-10%",
            background: `linear-gradient(180deg, transparent 0%, ${v.rippleColor || v.glow}88 50%, transparent 100%)`,
            animation: `hf-ripple ${v.rippleMs || 4}s ease-in-out infinite`,
            mixBlendMode: "soft-light",
            opacity: Math.max(0, Math.min(1, (v.rippleOp || 0) * intensity)),
          }}
        />
      )}

      {/* FLOW PASS — two large soft radials that pool and travel across the
          card on different paths/speeds. This is the layer that brings the
          churning, refracting "living foil" feel back (the faction look).
          Built from the recipe's flowColors so a tier can theme its own. */}
      {v.flow && (
        <>
          <div
            className="hf-flow-a"
            style={{
              position: "absolute",
              top: "-30%", left: "-30%", width: "160%", height: "160%",
              background: `radial-gradient(circle at 35% 35%, ${(v.flowColors && v.flowColors[0]) || v.glow}cc 0%, ${(v.flowColors && v.flowColors[1]) || v.glow}66 28%, transparent 60%)`,
              animation: `hf-flow-a ${v.flowMs || 7}s ease-in-out infinite`,
              mixBlendMode: v.blendShim || "color-dodge",
              opacity: Math.max(0, Math.min(1, (v.flowOp || 0) * intensity)),
              filter: "blur(6px)",
            }}
          />
          <div
            className="hf-flow-b"
            style={{
              position: "absolute",
              top: "-30%", left: "-30%", width: "160%", height: "160%",
              background: `radial-gradient(circle at 60% 55%, ${(v.flowColors && v.flowColors[2]) || v.glow}cc 0%, ${(v.flowColors && v.flowColors[1]) || v.glow}55 30%, transparent 62%)`,
              animation: `hf-flow-b ${v.flowMs2 || 11}s ease-in-out infinite`,
              mixBlendMode: v.blendShim || "color-dodge",
              opacity: Math.max(0, Math.min(1, (v.flowOp || 0) * 0.85 * intensity)),
              filter: "blur(7px)",
            }}
          />
        </>
      )}

      {/* Diagonal light sweep — the catch-the-angle glint. */}
      {v.sweep && (
        <div
          className="hf-sweep"
          style={{
            position: "absolute",
            inset: 0,
            background:
              "linear-gradient(105deg, transparent 0%, transparent 35%, rgba(232,184,75,0.35) 45%, rgba(255,255,255,0.55) 50%, rgba(232,184,75,0.35) 55%, transparent 65%, transparent 100%)",
            animation: `hf-sweep ${v.sweepMs}s ease-in-out infinite`,
            mixBlendMode: v.blendSwp || "overlay",
            opacity: sweepOpacity,
          }}
        />
      )}

      {/* SECOND sweep — a wider, slower wash trailing the primary glint, so
          the highlight reads as layered light rather than a single wiper. */}
      {v.sweep2 && (
        <div
          className="hf-sweep2"
          style={{
            position: "absolute",
            inset: 0,
            background:
              `linear-gradient(100deg, transparent 0%, transparent 30%, ${v.glow}33 42%, ${v.glow}55 50%, ${v.glow}33 58%, transparent 70%, transparent 100%)`,
            animation: `hf-sweep2 ${v.sweep2Ms || 6}s ease-in-out infinite`,
            mixBlendMode: v.blendSwp || "overlay",
            opacity: Math.max(0, Math.min(1, (v.sweepOp || 0.6) * 0.7 * intensity)),
          }}
        />
      )}

      {/* GLINT STAR — the "wink." A single small, very bright specular
          point that arcs across the card on its own path, independent of
          the spinning diamonds. This is the trickster's catch of light. */}
      {v.glint && (
        <div
          className="hf-glint"
          style={{
            position: "absolute",
            top: 0, left: 0,
            width: "22%", aspectRatio: "1 / 1",
            background: `radial-gradient(circle, ${v.glintColor || "#ffffff"}ff 0%, ${v.glintColor || "#ffffff"}88 22%, transparent 60%)`,
            animation: `hf-glint ${v.glintMs || 4}s ease-in-out infinite`,
            mixBlendMode: "screen",
            opacity: Math.max(0, Math.min(1, 0.9 * intensity)),
            filter: "blur(0.5px)",
          }}
        />
      )}

      {/* GRIN — a bright crescent that drifts up, flips, and fades like a
          Cheshire smile surfacing through the foil. J'ynxi's exclusive
          motion signature: the green that grins back. */}
      {v.grin && (
        <div
          className="hf-grin"
          style={{
            position: "absolute",
            left: "18%", right: "18%", bottom: "-20%", height: "50%",
            borderRadius: "0 0 100% 100% / 0 0 100% 100%",
            border: `2px solid ${v.grinColor || v.glow}`,
            borderTopColor: "transparent",
            borderLeftColor: "transparent",
            borderRightColor: "transparent",
            boxShadow: `0 0 12px ${v.grinColor || v.glow}aa`,
            animation: `hf-grin ${v.grinMs || 7}s ease-in-out infinite`,
            mixBlendMode: "screen",
            opacity: 0,
          }}
        />
      )}

      {/* VOID — holo_void's signature, the SVG "MAELSTROM." The smoke is
          REAL fractal noise (feTurbulence), animated INSIDE the SVG so it
          actually roils — unlike CSS gradient blobs, which were too smooth
          to show motion and too flat (they blocked the art). Masked into a
          translucent tunnel RING around an open, dark throat so the card
          still reads through. The whole disc slowly rotates. */}
      {v.voidTurb && (
        <div
          className="hf-void-ring"
          style={{
            position: "absolute",
            top: "50%", left: "50%",
            // oversized + circular so rotation never reveals an edge
            width: "170%", height: "170%",
            transform: "translate(-50%, -50%)",
            transformOrigin: "center",
            animation: `hf-void-ring ${v.voidTurbSpin || 26}s linear infinite`,
            mixBlendMode: "screen",
            opacity: Math.max(0, Math.min(1, (v.voidTurbOpacity || 0.55) * intensity)),
            // tunnel shape: nothing at dead center (throat) + faded at rim
            // so the smoke is a RING, never a full-card wash.
            WebkitMaskImage: "radial-gradient(circle, transparent 12%, #000 30%, #000 62%, transparent 84%)",
            maskImage: "radial-gradient(circle, transparent 12%, #000 30%, #000 62%, transparent 84%)",
          }}
        >
          <svg
            viewBox="0 0 300 300"
            preserveAspectRatio="xMidYMid slice"
            style={{ width: "100%", height: "100%", display: "block" }}
          >
            <defs>
              <filter id={voidFilterId} x="-20%" y="-20%" width="140%" height="140%">
                <feTurbulence
                  type="fractalNoise"
                  baseFrequency="0.03 0.045"
                  numOctaves="4"
                  seed="7"
                  result="noise"
                >
                  {/* roil: sweep the frequency over a wide range so the smoke
                      visibly churns (small change = invisible motion). */}
                  <animate
                    attributeName="baseFrequency"
                    dur="14s"
                    values="0.03 0.045; 0.05 0.028; 0.022 0.05; 0.03 0.045"
                    repeatCount="indefinite"
                  />
                </feTurbulence>
                {/* displace the violet field by the noise → wispy smoke edges.
                    Animate the scale so wisps stretch + contract = live churn. */}
                <feDisplacementMap in="SourceGraphic" in2="noise" xChannelSelector="R" yChannelSelector="G">
                  <animate attributeName="scale" dur="11s" values="40; 90; 55; 40" repeatCount="indefinite" />
                </feDisplacementMap>
                <feGaussianBlur stdDeviation="1.5" />
              </filter>
              <radialGradient id={voidGradId} cx="50%" cy="50%" r="50%">
                <stop offset="0%" stopColor={v.voidTurbColor || "#9d5cff"} stopOpacity="0" />
                <stop offset="32%" stopColor={v.voidTurbColor || "#9d5cff"} stopOpacity="0.95" />
                <stop offset="60%" stopColor={v.voidTurbColor2 || "#5b21b6"} stopOpacity="0.8" />
                <stop offset="100%" stopColor={v.voidTurbColor2 || "#5b21b6"} stopOpacity="0" />
              </radialGradient>
            </defs>
            {/* the violet field, run through the turbulence filter = smoke */}
            <circle cx="150" cy="150" r="150" fill={`url(#${voidGradId})`} filter={`url(#${voidFilterId})`} />
          </svg>
        </div>
      )}

      {/* THROAT — the dark gullet at the card's heart. MULTIPLY so it REMOVES
          light: the tunnel bottoms out in true dark. The tell that this is
          Void (eaten light), never a bright psionic core. */}
      {v.voidThroat && (
        <div
          className="hf-void-throat"
          style={{
            position: "absolute",
            top: "50%", left: "50%",
            width: "46%", height: "46%",
            transform: "translate(-50%, -50%)",
            transformOrigin: "center",
            borderRadius: "50%",
            background: `radial-gradient(circle at center, ${v.voidThroatColor || "#08000f"}cc 0%, ${v.voidThroatColor || "#08000f"}99 38%, ${v.voidThroatColor || "#08000f"}44 62%, ${v.voidThroatColor || "#08000f"}11 76%, transparent 88%)`,
            animation: `hf-void-throat ${v.voidThroatMs || 7}s ease-in-out infinite`,
            mixBlendMode: "multiply",
            // Lighter on purpose: a pool of darkness for the "void" read while
            // the art still shows through. Tune via voidThroatStrength.
            opacity: Math.max(0, Math.min(1, (v.voidThroatStrength ?? 0.62) * intensity)),
            filter: "blur(6px)",
          }}
        />
      )}

      {/* ── QUICKSILVER (silver) — liquid mercury settling. Two meniscus
          bands: primary undulates down, counter drifts up slower. The
          metal never sits still, but silver stays understated. */}
      {v.mercury && (
        <>
          <div
            className="hf-mercury-a"
            style={{
              position: "absolute",
              left: "-5%", right: "-5%", top: 0, height: "45%",
              background: `linear-gradient(180deg, transparent 0%, ${v.mercuryColor || "#e8eef4"}cc 42%, ${v.mercuryColor2 || "#9aa4b0"}88 58%, transparent 100%)`,
              animation: `hf-mercury-a ${v.mercuryMsA || 7.5}s ease-in-out infinite`,
              mixBlendMode: "screen",
              opacity: Math.max(0, Math.min(1, (v.mercuryOp || 0.5) * intensity)),
              filter: "blur(3px)",
            }}
          />
          <div
            className="hf-mercury-b"
            style={{
              position: "absolute",
              left: "-5%", right: "-5%", top: 0, height: "34%",
              background: `linear-gradient(180deg, transparent 0%, ${v.mercuryColor2 || "#9aa4b0"}99 50%, transparent 100%)`,
              animation: `hf-mercury-b ${v.mercuryMsB || 11}s ease-in-out infinite`,
              mixBlendMode: "screen",
              opacity: Math.max(0, Math.min(1, (v.mercuryOp || 0.5) * 0.7 * intensity)),
              filter: "blur(4px)",
            }}
          />
        </>
      )}

      {/* ── MOLTEN (gold) — the pour. A tongue of gold pours from the top
          seam, stretches, hangs, recedes. Flecks twinkle in the surface. */}
      {v.molten && (
        <div
          className="hf-molten"
          style={{
            position: "absolute",
            top: 0, left: "12%", right: "12%", height: "88%",
            transformOrigin: "center top",
            background: `linear-gradient(180deg, ${v.moltenColor || "#ffd700"}ee 0%, ${v.moltenColor || "#ffd700"}88 30%, ${v.moltenColor2 || "#b8860b"}55 62%, transparent 100%)`,
            borderRadius: "0 0 45% 55% / 0 0 30% 38%",
            animation: `hf-molten ${v.moltenMs || 6.5}s ease-in-out infinite`,
            mixBlendMode: "screen",
            opacity: Math.max(0, Math.min(1, (v.moltenOp || 0.55) * intensity)),
            filter: "blur(4px)",
          }}
        />
      )}
      {v.flecks && [[22, 34, 0], [68, 22, 1.1], [48, 66, 2.2]].map(([x, y, d], i) => (
        <div
          key={`fl${i}`}
          className="hf-fleck"
          style={{
            position: "absolute",
            left: `${x}%`, top: `${y}%`,
            width: "5%", aspectRatio: "1 / 1",
            background: `radial-gradient(circle, ${v.fleckColor || "#fff3c4"}ff 0%, ${v.fleckColor || "#fff3c4"}66 40%, transparent 70%)`,
            animation: `hf-fleck ${v.fleckMs || 3.2}s ease-in-out ${d}s infinite`,
            mixBlendMode: "screen",
            opacity: Math.max(0, Math.min(1, (v.fleckOp || 0.85) * intensity)),
          }}
        />
      ))}

      {/* ── THE GRIDDLE (vrown) — Sir H'osh Vrown's own. A diagonal sear
          crosses the card once per cycle, the gradient itself carrying
          the ramp: white-gold leading edge → amber core → a crisp
          umber-brown trailing crust, fading out the far side. Sizzle
          sparks (the fleck engine, warm-toned) and a low cooling glow
          (the corona engine) are declared on the recipe, not here —
          they're already generic accents; only the sear itself is new. */}
      {v.griddle && (
        <div
          className="hf-griddle"
          style={{
            position: "absolute",
            inset: "-15% -35%",
            background: `linear-gradient(100deg, transparent 0%, ${v.griddleColorA || "#fdf6e3"}ee 34%, ${v.griddleColorB || "#c17a2e"}dd 52%, ${v.griddleColorC || "#3a2210"}bb 68%, transparent 100%)`,
            animation: `hf-griddle ${v.griddleMs || 6.2}s ease-in-out infinite`,
            mixBlendMode: "overlay",
            opacity: Math.max(0, Math.min(1, (v.griddleOp || 0.62) * intensity)),
            filter: "blur(2.5px)",
          }}
        />
      )}

      {/* ── THE SHREDS — the grate inside the sear.
          Same element geometry and the SAME `hf-griddle` animation as
          the band above, so the two travel locked together and can
          never drift out of phase. The mask is what does the work:
          transparent across the leading half, opaque across the
          trailing half, so a shred fades UP as the heat reaches it.
          Multiply, because a hash brown browning is light being eaten,
          not light being added. Both mask properties are written —
          the unprefixed one is standard now, the -webkit- one keeps
          older WebKit from rendering an unmasked brown hatch across
          the whole card, which is the loud failure mode. */}
      {v.griddle && v.shreds && (
        <div
          className="hf-griddle hf-griddle-shred"
          style={{
            position: "absolute",
            inset: "-15% -35%",
            background: `repeating-linear-gradient(118deg, ${v.shredColor || "#5a3312"} 0 1.6px, transparent 1.6px 6px)`,
            WebkitMaskImage: "linear-gradient(100deg, transparent 38%, rgba(0,0,0,0.55) 52%, #000 66%, transparent 82%)",
            maskImage: "linear-gradient(100deg, transparent 38%, rgba(0,0,0,0.55) 52%, #000 66%, transparent 82%)",
            animation: `hf-griddle ${v.griddleMs || 6.2}s ease-in-out infinite`,
            mixBlendMode: "multiply",
            opacity: Math.max(0, Math.min(1, (v.shredOp || 0.22) * intensity)),
            filter: "blur(0.4px)",
          }}
        />
      )}

      {/* ── THE GRIP (gauntlet) — four plates that close on the card.
          Each plate is a gradient bar whose LIT EDGE faces the centre,
          so the light travels with the movement instead of sitting
          still on a moving object — the difference between a plate
          sliding and a plate being pushed. They stop short of the
          middle: what closes is a frame, not a lid, and the art stays
          readable through the whole gesture. */}
      {v.grip && ["t", "b", "l", "r"].map((side) => {
        const vertical = side === "t" || side === "b";
        const toward = side === "t" ? "180deg" : side === "b" ? "0deg" : side === "l" ? "90deg" : "270deg";
        return (
          <div
            key={`gp${side}`}
            className="hf-grip"
            style={{
              position: "absolute",
              ...(vertical
                ? { left: "-6%", right: "-6%", height: "34%", [side === "t" ? "top" : "bottom"]: 0 }
                : { top: "-6%", bottom: "-6%", width: "30%", [side === "l" ? "left" : "right"]: 0 }),
              background: `linear-gradient(${toward}, transparent 0%, ${v.gripPlate || "#c6cfd9"}88 42%, ${v.gripEdge || "#f2f7fb"}dd 88%, ${v.gripEmber || "#ff6a2a"}66 100%)`,
              animation: `hf-grip-${side} ${v.gripMs || 4.4}s cubic-bezier(.5,.02,.3,1) infinite`,
              mixBlendMode: "overlay",
              opacity: Math.max(0, Math.min(1, (v.gripOp || 0.55) * intensity)),
              filter: "blur(1.4px)",
            }}
          />
        );
      })}

      {/* The clench. A white flash and an ember knuckle at the exact
          frame the four plates meet — the only abrupt thing on any
          card in the game, and it lasts about a fifth of a second. */}
      {v.grip && v.clench && (
        <div
          className="hf-clench"
          style={{
            position: "absolute",
            inset: "18%",
            borderRadius: "42%",
            background: `radial-gradient(circle, ${v.clenchColor || "#ffffff"} 0%, ${v.gripEmber || "#ff6a2a"}aa 34%, transparent 72%)`,
            animation: `hf-clench ${v.gripMs || 4.4}s ease-out infinite`,
            mixBlendMode: "screen",
            opacity: Math.max(0, Math.min(1, (v.clenchOp || 0.5) * intensity)),
            filter: "blur(3px)",
          }}
        />
      )}

      {/* ── PRISM (mythic) — chromatic aberration. R, G, and B bands sweep
          at slightly different speeds, splitting apart and recombining
          into white where they cross. Real refraction, never a recolor. */}
      {v.prism && ["#ff3355", "#33ff88", "#3388ff"].map((c, i) => (
        <div
          key={`pr${i}`}
          className="hf-prism"
          style={{
            position: "absolute",
            inset: 0,
            background: `linear-gradient(115deg, transparent 40%, ${c}cc 50%, transparent 60%)`,
            animation: `hf-prism ${((v.prismMs || 4.6) * (1 + i * 0.13)).toFixed(2)}s ease-in-out ${(i * 0.18).toFixed(2)}s infinite`,
            mixBlendMode: "screen",
            opacity: Math.max(0, Math.min(1, (v.prismOp || 0.6) * intensity)),
            filter: "blur(1px)",
          }}
        />
      ))}

      {/* ── THE REND (solrend) — THE SHATTERED MARK (July 24 ruling).
          The straight seam is retired: the tear is now the seeded jagged
          bolt — geometry shared verbatim with the 3D texture painter
          (SOLREND_SHATTER_PTS above). Four nested strokes, wide dim red
          → orange → hot pale → white core, mirroring the native ribbon
          widths (26/13/5.5/2 on a 256 canvas → ~10/5/2.2/0.8 here).
          The EVENT envelope is kept: dark most of the cycle, tear,
          FLARE, seal. Pure CSS blur per stroke — no SVG filters, so the
          filter-ID law has nothing to collide. */}
      {v.rend && (
        <div
          className="hf-rend"
          style={{
            position: "absolute", inset: 0,
            transformOrigin: "center",
            animation: `hf-rendShatter ${v.rendMs || 5.4}s ease-in-out infinite`,
            mixBlendMode: "screen",
            opacity: 0, // keyframes own the visible opacity; this is the floor
            // Static intensity cap rides a separate wrapper-level channel:
            // strokes below bake (v.rendOp * intensity) into their alphas.
          }}
        >
          <svg viewBox="0 0 100 140" preserveAspectRatio="none"
               style={{ position: "absolute", inset: 0, width: "100%", height: "100%", overflow: "visible" }}>
            {(() => {
              const cap = Math.max(0, Math.min(1, (v.rendOp || 0.9) * intensity));
              const a = (x) => Math.max(0, Math.min(1, x * cap));
              const rc = v.rendColor || "#ff2266";
              const core = v.rendCore || "#ffe9ec";
              return (
                <>
                  <path d={SOLREND_SHATTER_D} fill="none" stroke={rc} strokeWidth="10"
                        strokeLinejoin="round" strokeLinecap="round"
                        style={{ opacity: a(0.30), filter: "blur(3px)" }} />
                  <path d={SOLREND_SHATTER_D} fill="none" stroke="#ff7a3c" strokeWidth="5"
                        strokeLinejoin="round" strokeLinecap="round"
                        style={{ opacity: a(0.55), filter: "blur(1.4px)" }} />
                  <path d={SOLREND_SHATTER_D} fill="none" stroke={core} strokeWidth="2.2"
                        strokeLinejoin="round" strokeLinecap="round"
                        style={{ opacity: a(0.9), filter: "blur(0.4px)" }} />
                  <path d={SOLREND_SHATTER_D} fill="none" stroke="#fffef6" strokeWidth="0.9"
                        strokeLinejoin="round" strokeLinecap="round"
                        style={{ opacity: a(1) }} />
                </>
              );
            })()}
          </svg>
        </div>
      )}
      {v.embers && [[30, 0], [55, 1.4], [72, 2.6], [42, 3.5]].map(([x, d], i) => (
        <div
          key={`em${i}`}
          className="hf-ember"
          style={{
            position: "absolute",
            left: `${x}%`, top: "55%",
            width: "3.5%", aspectRatio: "1 / 1",
            borderRadius: "50%",
            background: `radial-gradient(circle, ${v.emberColor || "#ff6a3d"}ff 0%, ${v.emberColor || "#ff6a3d"}44 60%, transparent 80%)`,
            animation: `hf-ember ${v.emberMs || 4.2}s ease-out ${d}s infinite`,
            mixBlendMode: "screen",
            opacity: Math.max(0, Math.min(1, (v.emberOp || 0.8) * intensity)),
          }}
        />
      ))}
      {v.corona && (
        <div
          className="hf-corona"
          style={{
            position: "absolute",
            left: "-10%", right: "-10%", bottom: "-12%", height: "48%",
            transformOrigin: "center bottom",
            background: `radial-gradient(ellipse at 50% 100%, ${v.coronaColor || "#cc0022"}bb 0%, ${v.coronaColor || "#cc0022"}44 45%, transparent 75%)`,
            animation: `hf-corona ${v.coronaMs || 4.8}s ease-in-out infinite`,
            mixBlendMode: "screen",
            opacity: Math.max(0, Math.min(1, (v.coronaOp || 0.4) * intensity)),
            filter: "blur(5px)",
          }}
        />
      )}

      {/* ── HALO (a'eterni) — the heartbeat of eternity. Two rings of
          white light, half a phase apart, expand from the heart and
          dissolve — the pulse never ends. Stars twinkle; the surface
          breathes luminous. Nothing rushes. */}
      {v.halo && [0, (v.haloMs || 6.8) / 2].map((d, i) => (
        <div
          key={`ha${i}`}
          className="hf-halo"
          style={{
            position: "absolute",
            top: "50%", left: "50%",
            width: "85%", aspectRatio: "1 / 1",
            borderRadius: "50%",
            border: `2px solid ${v.haloColor || "#f0f0ff"}`,
            boxShadow: `0 0 14px ${v.haloColor || "#f0f0ff"}88, inset 0 0 14px ${v.haloColor || "#f0f0ff"}55`,
            animation: `hf-halo ${v.haloMs || 6.8}s ease-out ${d}s infinite`,
            mixBlendMode: "screen",
            opacity: 0,
            transform: "translate(-50%, -50%) scale(0.15)",
          }}
        />
      ))}
      {v.twinkles && [[24, 26, 0], [70, 18, 1.6], [58, 74, 3.1]].map(([x, y, d], i) => (
        <div
          key={`tw${i}`}
          className="hf-twinkle"
          style={{
            position: "absolute",
            left: `${x}%`, top: `${y}%`,
            width: "4%", aspectRatio: "1 / 1",
            background: `radial-gradient(circle, ${v.twinkleColor || "#ffffff"}ff 0%, transparent 65%)`,
            boxShadow: `0 0 6px ${v.twinkleColor || "#ffffff"}cc`,
            animation: `hf-twinkle ${v.twinkleMs || 4.4}s ease-in-out ${d}s infinite`,
            mixBlendMode: "screen",
            opacity: Math.max(0, Math.min(1, (v.twinkleOp || 0.9) * intensity)),
          }}
        />
      ))}
      {v.breathe && (
        <div
          className="hf-breathe"
          style={{
            position: "absolute",
            inset: 0,
            background: `radial-gradient(ellipse at 50% 45%, ${v.breatheColor || "#e6e6ff"}, transparent 78%)`,
            animation: `hf-breathe ${v.breatheMs || 7.5}s ease-in-out infinite`,
            mixBlendMode: "screen",
            opacity: Math.max(0, Math.min(1, (v.breatheOp || 0.16) * intensity)),
          }}
        />
      )}

      {/* ── ECLIPSE (shadow) — the passing dark. A lobe of darkness
          crosses the card (MULTIPLY — it EATS light) while a violet
          rim-light circles the edge in step. Every other foil adds
          light; Shadow removes it. */}
      {v.eclipse && (
        <div
          className="hf-eclipse"
          style={{
            position: "absolute",
            top: "-15%", left: "-15%", width: "130%", height: "130%",
            background: `radial-gradient(circle at 50% 50%, ${v.eclipseColor || "#0a0018"}ee 0%, ${v.eclipseColor || "#0a0018"}99 38%, transparent 68%)`,
            animation: `hf-eclipse ${v.eclipseMs || 8.5}s ease-in-out infinite`,
            mixBlendMode: "multiply",
            opacity: Math.max(0, Math.min(1, (v.eclipseOp || 0.7) * intensity)),
            filter: "blur(6px)",
          }}
        />
      )}
      {v.rim && (
        <div
          className="hf-rim"
          style={{
            position: "absolute",
            inset: 0,
            background: `conic-gradient(from 0deg, transparent 0deg, ${v.rimColor || "#9966ff"}dd 35deg, ${v.rimColor || "#9966ff"}55 70deg, transparent 110deg, transparent 360deg)`,
            WebkitMaskImage: "radial-gradient(closest-side, transparent 72%, #000 88%, #000 100%)",
            maskImage: "radial-gradient(closest-side, transparent 72%, #000 88%, #000 100%)",
            animation: `hf-rim ${v.rimMs || 8.5}s linear infinite`,
            mixBlendMode: "screen",
            opacity: Math.max(0, Math.min(1, (v.rimOp || 0.75) * intensity)),
          }}
        />
      )}

      {/* ── CAUSTICS (depths) — the Gatekeeper's light-web. REAL SVG
          turbulence thresholded into thin bright cyan filaments — the
          dancing net of light on a pool floor. The web churns via SMIL
          inside the filter; out here it only drifts slowly upward.
          A different noise config entirely from Void's smoke: light
          instead of dark, web instead of ring, no rotation. */}
      {v.caustics && (
        <div
          className="hf-caustic"
          style={{
            position: "absolute",
            top: "-10%", left: "-5%", width: "110%", height: "120%",
            animation: `hf-caustic-drift ${v.causticDriftMs || 13}s ease-in-out infinite alternate`,
            mixBlendMode: "screen",
            opacity: Math.max(0, Math.min(1, (v.causticOp || 0.5) * intensity)),
            WebkitMaskImage: "radial-gradient(ellipse at 50% 50%, #000 55%, transparent 92%)",
            maskImage: "radial-gradient(ellipse at 50% 50%, #000 55%, transparent 92%)",
          }}
        >
          <svg
            viewBox="0 0 300 420"
            preserveAspectRatio="xMidYMid slice"
            style={{ width: "100%", height: "100%", display: "block" }}
          >
            <defs>
              <filter id={causticFilterId} x="-10%" y="-10%" width="120%" height="120%">
                <feTurbulence
                  type="turbulence"
                  baseFrequency="0.018 0.028"
                  numOctaves="2"
                  seed="11"
                  result="noise"
                >
                  {/* churn: sweep the frequency so the web visibly dances */}
                  <animate
                    attributeName="baseFrequency"
                    dur="12s"
                    values="0.018 0.028; 0.027 0.019; 0.014 0.033; 0.018 0.028"
                    repeatCount="indefinite"
                  />
                </feTurbulence>
                {/* threshold the noise into thin bright cyan filaments:
                    constant cyan color, alpha = steep ramp on the blue
                    channel → only the noise ridges survive = light-web. */}
                <feColorMatrix
                  in="noise"
                  type="matrix"
                  values="0 0 0 0 0.5   0 0 0 0 0.85   0 0 0 0 1   0 0 9 0 -3.6"
                />
                <feGaussianBlur stdDeviation="0.7" />
              </filter>
            </defs>
            <rect width="300" height="420" fill={v.causticColor || "#7fd8ff"} filter={`url(#${causticFilterId})`} />
          </svg>
        </div>
      )}
      {v.bubbles && [[20, 0], [50, 2.2], [76, 4.1]].map(([x, d], i) => (
        <div
          key={`bu${i}`}
          className="hf-bubble"
          style={{
            position: "absolute",
            left: `${x}%`, top: "82%",
            width: "4%", aspectRatio: "1 / 1",
            borderRadius: "50%",
            border: `1px solid ${v.bubbleColor || "#bfeaff"}cc`,
            background: `radial-gradient(circle at 35% 30%, ${v.bubbleColor || "#bfeaff"}88, transparent 60%)`,
            animation: `hf-bubble ${v.bubbleMs || 6.5}s ease-in ${d}s infinite`,
            mixBlendMode: "screen",
            opacity: Math.max(0, Math.min(1, (v.bubbleOp || 0.7) * intensity)),
          }}
        />
      ))}
      {v.tide && (
        <div
          className="hf-tide"
          style={{
            position: "absolute",
            left: 0, right: 0, bottom: "-6%", height: "42%",
            background: `linear-gradient(180deg, transparent 0%, ${v.tideColor || "#003a66"}99 55%, ${v.tideColor || "#003a66"}dd 100%)`,
            animation: `hf-tide ${v.tideMs || 7.2}s ease-in-out infinite`,
            mixBlendMode: "multiply",
            opacity: Math.max(0, Math.min(1, (v.tideOp || 0.45) * intensity)),
            filter: "blur(4px)",
          }}
        />
      )}

      {/* ── PILLARS (nihil) — standing white columns with a cascade of
          tiny black diamonds falling inside them. Columns GLOW (screen)
          and breathe; motes EAT light (multiply) and stream downward.
          Three columns, three motes each, all staggered so the void-rain
          never falls in lockstep. Distinct from every other motion:
          vertical light + descending dark, no rotation of the field. */}
      {v.pillars && [{ x: 22, d: 0 }, { x: 50, d: 1.6 }, { x: 78, d: 0.9 }].map((col, ci) => (
        <React.Fragment key={`np${ci}`}>
          {/* the breathing column of white light */}
          <div
            className="hf-nihil-pillar"
            style={{
              position: "absolute",
              left: `${col.x}%`, top: 0, bottom: 0,
              width: "14%",
              transform: "translateX(-50%)",
              transformOrigin: "center",
              background: `linear-gradient(90deg, transparent 0%, ${v.pillarColor || "#f4f4f8"}66 35%, ${v.pillarColor || "#f4f4f8"}aa 50%, ${v.pillarColor || "#f4f4f8"}66 65%, transparent 100%)`,
              animation: `hf-nihil-pillar ${v.pillarMs || 4.6}s ease-in-out ${col.d}s infinite`,
              mixBlendMode: "screen",
              opacity: Math.max(0, Math.min(1, (v.pillarOp || 0.5) * intensity)),
              filter: "blur(2px)",
            }}
          />
          {/* the black diamonds cascading down that column */}
          {v.motes && [0, 1, 2].map((m) => (
            <div
              key={`nm${ci}-${m}`}
              className="hf-nihil-mote"
              style={{
                position: "absolute",
                left: `${col.x}%`, top: "-18%",
                width: "3.5%", aspectRatio: "1 / 1",
                background: `linear-gradient(135deg, ${v.moteColor || "#08080e"}ff, ${v.moteColor || "#08080e"}cc)`,
                boxShadow: `0 0 3px ${v.moteColor || "#08080e"}`,
                transform: "translateX(-50%) rotate(45deg)",
                animation: `hf-nihil-fall ${v.moteMs || 3.8}s linear ${(col.d + m * ((v.moteMs || 3.8) / 3)).toFixed(2)}s infinite`,
                mixBlendMode: "multiply",
                opacity: Math.max(0, Math.min(1, (v.moteOp || 0.85) * intensity)),
              }}
            />
          ))}
        </React.Fragment>
      ))}

      {/* ── BULLSEYE (omnia) — concentric black/white target reticles that
          surface, watch, and vanish at scattered points. Each is three
          nested ring borders (dark/light/dark) so it reads as a true
          bullseye; the wrapper pops via hf-omnia-pop with a per-reticle
          delay so the field of eyes never blinks in sync. Rings on
          multiply so the dark reads as bored-through voids on the art. */}
      {v.bullseye && [
        { x: 30, y: 28, s: 1.0, d: 0 },
        { x: 72, y: 22, s: 0.75, d: 1.5 },
        { x: 24, y: 70, s: 0.85, d: 2.7 },
        { x: 68, y: 66, s: 1.05, d: 0.9 },
        { x: 50, y: 48, s: 0.9, d: 3.4 },
      ].map((b, bi) => (
        <div
          key={`ob${bi}`}
          className="hf-omnia"
          style={{
            position: "absolute",
            left: `${b.x}%`, top: `${b.y}%`,
            width: `${22 * b.s}%`, aspectRatio: "1 / 1",
            transform: "translate(-50%, -50%) scale(0)",
            animation: `hf-omnia-pop ${v.bullseyeMs || 4.4}s ease-in-out ${b.d}s infinite`,
            mixBlendMode: "multiply",
            opacity: 0,
          }}
        >
          {/* outer dark ring */}
          <div style={{
            position: "absolute", inset: 0, borderRadius: "50%",
            border: `1.5px solid ${v.ringDark || "#08080e"}`,
            boxShadow: `0 0 4px ${v.ringDark || "#08080e"}aa`,
          }} />
          {/* middle light ring */}
          <div style={{
            position: "absolute", inset: "26%", borderRadius: "50%",
            border: `1.5px solid ${v.ringLight || "#f0f0f4"}`,
            mixBlendMode: "screen",
          }} />
          {/* inner dark pip */}
          <div style={{
            position: "absolute", inset: "42%", borderRadius: "50%",
            background: v.ringDark || "#08080e",
          }} />
        </div>
      ))}

      {/* Corner flourishes — the signature mark. */}
      {showCorners && (
        <>
          <span style={cornerStyle("tl", cornerColor)} />
          <span style={cornerStyle("br", cornerColor)} />
        </>
      )}
    </div>
  );
}

function cornerStyle(which, color) {
  const base = {
    position: "absolute",
    width: 10,
    height: 10,
    pointerEvents: "none",
    filter: `drop-shadow(0 0 3px ${color})`,
  };
  if (which === "tl") {
    return { ...base, top: 2, left: 2, borderTop: `1.5px solid ${color}`, borderLeft: `1.5px solid ${color}` };
  }
  return { ...base, bottom: 2, right: 2, borderBottom: `1.5px solid ${color}`, borderRight: `1.5px solid ${color}` };
}

// Re-export the glow keyframes injector so a screen using holoGlowStyle()
// can mount it once near its root: <HoloGlowDefs tier={…} />
export { GlowKeyframes as HoloGlowDefs };
